/**
 * Dominant color extraction for event images, backed by ColorThief.
 *
 * Local files (blob: URLs) and remote/API images (http(s) URLs, e.g. Cloudinary)
 * are both supported. Remote images are loaded with crossOrigin="anonymous" so
 * ColorThief can read canvas pixel data without tainting the canvas — this
 * requires the image host to send CORS headers (Cloudinary delivery URLs do
 * by default).
 */
import { getColor } from 'colorthief';

export type RGBColor = [number, number, number];

function clampChannel(value: number): number {
  return Math.max(0, Math.min(255, Math.round(value)));
}

export function rgbToHex([r, g, b]: RGBColor): string {
  return `#${[r, g, b].map((c) => clampChannel(c).toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Normalize any of the shapes a dominant color might arrive in — a hex
 * string, an `rgb()`/`rgba()` string, or a raw [r, g, b] array — into a
 * single canonical lowercase hex string so values can be compared reliably
 * regardless of where they came from (freshly extracted vs. stored).
 */
export function normalizeColor(input: string | number[] | null | undefined): string | null {
  if (input == null) return null;
  if (Array.isArray(input)) {
    if (input.length < 3) return null;
    return rgbToHex([Number(input[0]), Number(input[1]), Number(input[2])]);
  }
  const value = String(input).trim();
  if (!value) return null;
  if (value.startsWith('#')) {
    let hex = value.slice(1);
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map((c) => c + c)
        .join('');
    }
    if (hex.length !== 6) return null;
    return `#${hex.toLowerCase()}`;
  }
  const rgbMatch = value.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgbMatch) {
    return rgbToHex([Number(rgbMatch[1]), Number(rgbMatch[2]), Number(rgbMatch[3])]);
  }
  return value.toLowerCase();
}

export function colorsEqual(
  a: string | number[] | null | undefined,
  b: string | number[] | null | undefined,
): boolean {
  return normalizeColor(a) === normalizeColor(b);
}

/** True if the value normalizes to a real "#rrggbb" color — false for missing/malformed input. */
export function isValidColor(value: string | number[] | null | undefined): boolean {
  return normalizeColor(value) !== null;
}

/**
 * The app's default event accent color, used as the fallback so pages never
 * go un-themed when an event has no valid `dominant_color`.
 */
export const DEFAULT_EVENT_ACCENT_COLOR = '#ffffff';

/** Validate + normalize a stored/backend dominant color, falling back to the app default when invalid. */
export function resolveEventAccentColor(
  dominantColor: string | number[] | null | undefined,
  fallback: string = DEFAULT_EVENT_ACCENT_COLOR,
): string {
  return normalizeColor(dominantColor) ?? fallback;
}

function hexToRgbTuple(hex: string): RGBColor | null {
  const normalized = normalizeColor(hex);
  if (!normalized) return null;
  const int = parseInt(normalized.slice(1), 16);
  return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
}

/**
 * Pick whichever of white/black gives better WCAG contrast against a given
 * background color — used so button text/icons stay readable no matter how
 * dark, light, saturated, or muted an event's dominant color is.
 */
export function getContrastTextColor(hex: string): '#ffffff' | '#000000' {
  const rgb = hexToRgbTuple(hex);
  if (!rgb) return '#ffffff';
  const [rl, gl, bl] = rgb.map((c) => {
    const channel = c / 255;
    return channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
  });
  const luminance = 0.2126 * rl + 0.7152 * gl + 0.0722 * bl;
  const contrastWithWhite = 1.05 / (luminance + 0.05);
  const contrastWithBlack = (luminance + 0.05) / 0.05;
  return contrastWithWhite >= contrastWithBlack ? '#ffffff' : '#000000';
}

/** Mix a color toward black by `amount` (0–1) — e.g. a dark track color behind a progress ring. */
export function mixWithBlack(hex: string, amount: number): string {
  const rgb = hexToRgbTuple(hex);
  if (!rgb) return hex;
  return rgbToHex(rgb.map((c) => Math.round(c * (1 - amount))) as RGBColor);
}

function isCrossOriginUrl(url: string): boolean {
  if (url.startsWith('blob:') || url.startsWith('data:')) return false;
  try {
    return new URL(url, window.location.href).origin !== window.location.origin;
  } catch {
    return false;
  }
}

function loadImageElement(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (isCrossOriginUrl(url)) {
      img.crossOrigin = 'anonymous';
    }
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image for color extraction: ${url}`));
    img.src = url;
  });
}

/**
 * Extract the dominant color from an image URL (blob:, data:, or remote
 * http(s)). Returns null (never throws) if extraction fails — callers should
 * treat that as "keep whatever color we already have."
 */
export async function extractDominantColorFromUrl(url: string): Promise<string | null> {
  if (!url) return null;
  try {
    const img = await loadImageElement(url);
    const color = await getColor(img);
    return color ? color.hex() : null;
  } catch (err) {
    console.warn('[dominantColor] extraction failed for', url, err);
    return null;
  }
}

/** Extract the dominant color directly from a local File (e.g. a fresh upload). */
export async function extractDominantColorFromFile(file: File): Promise<string | null> {
  const objectUrl = URL.createObjectURL(file);
  try {
    return await extractDominantColorFromUrl(objectUrl);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * Re-check the dominant color of the current first image right before a
 * save/create/update request goes out, so the payload always reflects the
 * image that's actually first — even if the user reordered/replaced images
 * without the live tracker catching up yet.
 *
 * Falls back to the normalized stored color if extraction fails (e.g. a
 * transient CORS/network issue), so a save is never blocked by this check.
 */
export async function resolveDominantColorForSubmit(
  primaryImageUrl: string | null | undefined,
  storedColor: string | null | undefined,
): Promise<string | null> {
  if (!primaryImageUrl) return null;
  const extracted = await extractDominantColorFromUrl(primaryImageUrl);
  if (extracted) return extracted;
  return normalizeColor(storedColor ?? null);
}
